import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Package } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import VoiceInput from "@/components/VoiceInput";
import LazyItemPhoto from "@/components/LazyItemPhoto";
import { formatLocationPath } from "@/lib/locationPath";

export default function Search() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => { api.get("/locations").then(({ data }) => setLocations(data)); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      const { data } = await api.get("/items", { params: { q: q.trim() } });
      setResults(data);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-8" data-testid="search-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Where is…?</h1>
        <p className="mt-2 text-stoneink-700">Ask out loud or type a few letters.</p>
      </div>

      <div className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stoneink-500" />
          <input
            data-testid="search-input"
            autoFocus
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Holiday lights, passport, batteries…"
            className="w-full h-14 pl-12 pr-4 text-lg rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
          />
        </div>
        <VoiceInput onTranscript={(t) => setQ((p) => (p ? p + " " : "") + t)} testid="search-voice-btn" />
      </div>

      {q && results.length === 0 && (
        <div className="text-stoneink-500 text-sm">No matches yet. Try a different word?</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {results.map((it, i) => {
          const path = formatLocationPath(locations, it.location_id);
          return (
            <motion.div key={it.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03 }}>
              <Link
                to={`/items/${it.id}`}
                data-testid={`search-result-${it.id}`}
                className="block rounded-3xl border border-sand-300 bg-white shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all overflow-hidden"
              >
                <div className="aspect-[5/3] bg-sand-200">
                  {it.has_photo ? (
                    <LazyItemPhoto itemId={it.id} hasPhoto={it.has_photo} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <Package weight="duotone" size={36} className="text-stoneink-500/60" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="font-heading text-lg text-stoneink-900 font-medium truncate">{it.name}</div>
                  <div className="text-sm text-terracotta-600 mt-1 truncate" title={path}>
                    {path ? `→ ${path}` : "No location"}
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
